import type { EstimatableCarrierCode, EstimateFeesPayload } from '@/services/shipping.service';

export interface BuildEstimateInput {
  providers: readonly EstimatableCarrierCode[];
  storeId?: string;
  /**
   * Nơi lấy hàng. BẮT BUỘC mới hỏi phí.
   *
   * Chi nhánh nhiều địa chỉ gửi mà chỉ đưa `store_id` thì BE `findOne` được bản
   * ghi nào cũng được — phí báo về tính từ một địa chỉ tuỳ ý, lệch thật khi hai
   * kho khác tỉnh. Thà chưa có số còn hơn có số sai.
   */
  pickupLocationId?: string;
  /** `form.getFieldsValue(true)` của POS. */
  values: Record<string, unknown>;
  /** COD tính trên tiền hàng − trả trước, KHÔNG cộng phí ship (phí chưa biết). */
  cod: number;
}

function num(value: unknown): number | undefined {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/**
 * Form POS → payload hỏi phí. Trả `null` khi chưa đủ dữ liệu — nơi gọi dùng
 * chính `null` đó làm cổng "chưa được phép hỏi hãng", nên không có chỗ nào khác
 * phải tự viết lại điều kiện đủ.
 *
 * ĐÂY là nơi DUY NHẤT phía FE quy đổi kg→gram và cm→milimet. Rải phép nhân đó
 * ra component là cách chắc chắn nhất để hai chỗ trôi lệch nhau.
 */
export function buildEstimatePayload(input: BuildEstimateInput): EstimateFeesPayload | null {
  const { providers, storeId, pickupLocationId, values, cod } = input;
  if (providers.length === 0) return null;
  if (!pickupLocationId) return null;

  const cityId = (values.delivery_city_id as string | undefined) ?? (values.customer_city_id as string | undefined);
  const wardId = (values.delivery_ward_id as string | undefined) ?? (values.customer_ward_id as string | undefined);
  const address = (values.delivery_address as string | undefined) ?? (values.customer_address as string | undefined);
  const weightKg = num(values.package_weight);

  if (!cityId || !address?.trim() || !weightKg) return null;

  const insuranceEnabled = values.shipping_insurance_enabled === true;
  const insuranceValue = Number(values.shipping_insurance_value ?? 0);
  const lengthCm = num(values.package_length);
  const widthCm = num(values.package_width);
  const heightCm = num(values.package_height);

  return {
    providers: [...providers],
    ...(storeId ? { store_id: storeId } : {}),
    pickup_location_id: pickupLocationId,
    receiver_address: address.trim(),
    receiver_city_id: cityId,
    ...(wardId ? { receiver_ward_id: wardId } : {}),
    weight: Math.max(1, Math.round(weightKg * 1000)),
    ...(lengthCm ? { length: Math.round(lengthCm * 10) } : {}),
    ...(widthCm ? { width: Math.round(widthCm * 10) } : {}),
    ...(heightCm ? { height: Math.round(heightCm * 10) } : {}),
    value: insuranceEnabled && insuranceValue > 0 ? insuranceValue : 0,
    cod: Math.max(0, Math.round(cod)),
  };
}
